/**
 * The team profile's Stats tab, reduced from one league table to one team.
 *
 * WHERE THE NUMBERS COME FROM. sports-api owns no team season stat record. The
 * only per team statistics this platform stores are the cells of a standings
 * row, and the only way to read them is POST /standings/get, which answers for
 * a whole league. So this tab loads the league table the team sits in and keeps
 * one row out of it. Every number arrives as a rendered string in `display` and
 * every heading arrives as `label`. Nothing in this file adds, divides,
 * formats, rounds or names a statistic.
 *
 * `value` is on the wire and is deliberately not read. It is the numeric form,
 * and the moment this file reads it somebody will sort or compute with it,
 * which is what `display` exists to prevent.
 *
 * THE WIRE TYPES ARE IMPORTED, NOT REDECLARED. `../standings/standingsTypes`
 * already mirrors POST /standings/get one field for one field for the web
 * standings screen, and that is the same response this file reads. A private
 * copy here would be a second thing to update the next time a column changes.
 * apps/mobile keeps its own copies in `teamStatsView.ts` because it has no web
 * standings screen to share with.
 *
 * A column with no matching cell is dropped, and a cell with no matching
 * column is dropped too. Those are not the same rule for the same reason. A
 * column with no cell has a heading and no number, which is a blank row. A cell
 * with no column has a number and no heading, and the only way to print it
 * would be to invent a label for a key like "strk", which is exactly the copy
 * composition the season's declared column set exists to keep on the server.
 *
 * No React here. Both reducers are pure and are tested directly.
 */
import type {
  typeStandingsCellDto,
  typeStandingsColumn,
  typeStandingsDataDto,
  typeStandingsGroupDto,
  typeStandingsRowDto,
} from "../standings/standingsTypes";

/** One printed line of the tab. Both strings are server rendered. */
export type typeTeamStatRow = {
  key: string;
  label: string;
  display: string;
};

export type typeTeamStatsView = {
  /** The season this table is for, for example "2025 season". */
  seasonLabel: string;
  /** Where the team sits inside its group, for example "3rd". */
  rankLabel: string;
  /** The group the rank is measured in, for example "AFC West". */
  groupLabel: string;
  /** At most three per season, the ones the league calls headline. */
  primary: typeTeamStatRow[];
  secondary: typeTeamStatRow[];
};

/** The tab's own heading. One of the three strings on this tab the app writes. */
export const TEAM_STATS_TITLE = "Season stats";

/** Announced while POST /standings/get is still in flight. */
export const TEAM_STATS_LOADING_LABEL = "Loading the season stats";

/**
 * Shown when the server put the Stats tab in the strip and the table this tab
 * prints from did not arrive.
 *
 * The server decides the key, from the standings row it stores, while the
 * numbers come from POST /standings/get one round trip later. Two answers to
 * that second call land here, and this line covers both instead of a blank
 * panel. One is a failed or empty table. The other is narrower and is named in
 * services/sports-api/src/lib/resolveTeamProfileTabs.ts: a team ranked only
 * outside the season's default grouping has a standings row the server can see
 * and no row in the table this file reads.
 *
 * It offers no reason, because a fan cannot act on one, and it does not say
 * error, because a league table that failed to load is not this fan's problem
 * to solve.
 */
export const TEAM_STATS_UNAVAILABLE_LABEL = "Season stats aren't here yet";

/**
 * This team's row and the group it was found in, or null.
 *
 * Searches every group rather than assuming the default grouping puts each team
 * in exactly one: a league that groups by conference and by division answers a
 * different table per `grouping`, and this reads whichever one the server chose.
 * Strict equality on the id, no trimming and no case folding, because a near
 * match means we are looking at the wrong team.
 */
export function findTeamStandingsRow(
  data: typeStandingsDataDto,
  teamId: string,
): { row: typeStandingsRowDto; group: typeStandingsGroupDto } | null {
  if (teamId.length === 0) {
    return null;
  }
  for (const group of data.groups) {
    for (const row of group.rows) {
      if (row.teamId === teamId) {
        return { row, group };
      }
    }
  }
  return null;
}

/**
 * Zips one column set against one row's cells, in the column order the season
 * declared. A column with no cell and a cell with no column are both dropped;
 * the file header says why each is dropped for a different reason.
 */
export function zipStatRows(
  columns: readonly typeStandingsColumn[],
  cells: readonly typeStandingsCellDto[],
): typeTeamStatRow[] {
  const displayByKey = new Map<string, string>();
  for (const cell of cells) {
    if (!displayByKey.has(cell.key)) {
      displayByKey.set(cell.key, cell.display);
    }
  }

  const rows: typeTeamStatRow[] = [];
  for (const column of columns) {
    const display = displayByKey.get(column.key);
    if (display === undefined) {
      continue;
    }
    rows.push({ key: column.key, label: column.label, display });
  }
  return rows;
}

/**
 * The whole tab, or null when this league table holds no row for this team.
 *
 * Null is the answer that draws the unavailable line, and it is the right
 * answer for every league that keeps no table: golf and MMA have no standings
 * row for anybody. A table the server marked empty is null too, because an
 * `emptyReason` means the season has not started and there is nothing to show.
 */
export function toTeamStatsView(
  data: typeStandingsDataDto,
  teamId: string,
): typeTeamStatsView | null {
  const emptyReason = data.emptyReason ?? null;
  if (emptyReason !== null && emptyReason.length > 0) {
    return null;
  }

  const found = findTeamStandingsRow(data, teamId);
  if (found === null) {
    return null;
  }

  const primary = zipStatRows(data.primaryColumns, found.row.cells);
  const secondary = zipStatRows(data.secondaryColumns, found.row.cells);

  // A row that zips to nothing is a row with a rank and no statistics. The rank
  // alone is already in the header's standingLabel, so a panel holding only a
  // copy of it has nothing of its own to print.
  if (primary.length === 0 && secondary.length === 0) {
    return null;
  }

  return {
    seasonLabel: data.seasonLabel,
    rankLabel: found.row.rankLabel,
    groupLabel: found.group.label,
    primary,
    secondary,
  };
}
